/**
 * useEndorsements — fetch endorsements received by a member, plus a helper
 * to give one. RLS allows any authenticated member to read endorsements
 * (they're public-by-design) and only insert/delete their own.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export interface Endorsement {
  id: string;
  giver_id: string;
  receiver_id: string;
  quality: string;
  match_id: string | null;
  note: string | null;
  created_at: string;
}

export interface EndorsementSummary {
  quality: string;
  count: number;
}

export function useEndorsements(receiverId?: string | null) {
  const [items, setItems] = useState<Endorsement[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!receiverId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from("endorsements")
      .select("*")
      .eq("receiver_id", receiverId)
      .order("created_at", { ascending: false });
    setItems((data as Endorsement[] | null) ?? []);
    setLoading(false);
  }, [receiverId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const summary: EndorsementSummary[] = (() => {
    const map = new Map<string, number>();
    items.forEach((e) => map.set(e.quality, (map.get(e.quality) ?? 0) + 1));
    return Array.from(map.entries())
      .map(([quality, count]) => ({ quality, count }))
      .sort((a, b) => b.count - a.count);
  })();

  return { items, summary, loading, reload };
}

export function useGiveEndorsement() {
  const { user } = useAuth();

  const give = useCallback(
    async (
      receiverId: string,
      qualities: string[],
      opts: { matchId?: string | null; note?: string | null } = {},
    ): Promise<{ ok: boolean; error?: string }> => {
      if (!user) return { ok: false, error: "Not signed in." };
      if (receiverId === user.id) return { ok: false, error: "You can't endorse yourself." };
      if (qualities.length === 0) return { ok: false, error: "Pick at least one quality." };

      const rows = qualities.map((quality) => ({
        giver_id: user.id,
        receiver_id: receiverId,
        quality,
        match_id: opts.matchId ?? null,
        note: opts.note ?? null,
      }));
      const { error } = await supabase
        .from("endorsements")
        .upsert(rows, { onConflict: "giver_id,receiver_id,quality", ignoreDuplicates: true });
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    },
    [user],
  );

  return { give };
}
